var Component                  = require("enyo/Component"),
    UserRepository             = require("app/Domain/Ott/Api/V2/DataAccess/User/Repository"),
    UserService                = require("app/Domain/Ott/Api/V2/DataAccess/User/Service"),
    UserSessionRepository      = require("app/Domain/Ott/Api/V2/DataAccess/UserSession/Repository"),
    UserSessionService         = require("app/Domain/Ott/Api/V2/DataAccess/UserSession/Service"),
    UserSubscriptionRepository = require("app/Domain/Ott/Api/V2/DataAccess/UserSubscription/Repository"),
    UserSubscriptionService    = require("app/Domain/Ott/Api/V2/DataAccess/UserSubscription/Service"),
    EventEmitterSupport        = require("enyo-ui/EventEmitterSupport"),
    config                     = require("app/config"),
    get                        = require("object-path").get,
    kind                       = require("enyo/kind"),
    logger                     = require("enyo/logger");

module.exports = kind(/** @lends  app.Auth.AuthService.prototype */ {

    /**
     * @private
     */
    name: "app.Auth.AuthService",

    /**
     * @private
     */
    kind: Component,

    /**
     * @private
     */
    mixins: [
        EventEmitterSupport
    ],

    /**
     * The current login status. Status can have the following values:
     *  - `-1` = logged out
     *  -  `0` = status is still currently being determined
     *  -  `1` = logged in
     *
     * @type     {number}
     * @default  -1
     * @readonly
     * @public
     */
    isLoggedIn: -1,

    /**
     * The current user, if any.
     *
     * @type  {?Object}
     * @readonly
     * @public
     */
    user: null,

    /**
     * The current session key, if any.
     *
     * @type  {?Object}
     * @readonly
     * @public
     */
    sessionKey: null,

    // ----------------------------------
    // Startup
    // ----------------------------------

    /**
     * Verifies if the visitor is an adult.
     *
     * @returns  {boolean|null}  Whether the user is an adult, `null` if
     *                           unknown.
     * @public
     */
    isAdult: function() {
        var isAdult;

        if (this.isLoggedIn === 1) {
            return get(this, "user.is_adult", false);
        }

        try {
            isAdult = config.ottApi.useLocalStorage ? localStorage.getItem("ottVisitorIsAdult") : null;
        }
        catch (e) {
            isAdult = null;
        }

        switch (isAdult) {
            case "true":
                return true;
            case "false":
                return false;
            default:
                return null;
        }
    },

    /**
     * Update the current authentication status.
     *
     * @returns  {Promise}  A promise that will resolve with the result.
     * @public
     */
    updateStatus: function() {
        var sessionKey;

        try {
            sessionKey = config.ottApi.useLocalStorage ? localStorage.getItem("ottApiV2SessionKey") : null;
        }
        catch (e) {
            sessionKey = null;
        }

        // Use session key to resume session.
        if (sessionKey && this.isLoggedIn !== 1) {
            return this.resumeSession(sessionKey);
        }

        // Or just verify the current session using cookies
        else {
            return this.verifySession();
        }
    },

    /**
     * Return the current status.
     *
     * @return  {Object}  The current authentication status
     * @public
     */
    getStatus: function() {
        switch (this.isLoggedIn) {
            case 1:
                return {
                    isLoggedIn: 1,
                    user:       this.user,
                    sessionKey: this.sessionKey
                };
            case -1:
                return {
                    isLoggedIn: -1,
                    user:       null,
                    sessionKey: null
                };
            case 0:
            default:
                return {
                    isLoggedIn: 0,
                    user:       null,
                    sessionKey: null
                };
        }
    },

    /**
     * Returns an object containing all user and subscription properties that
     * can influence which media items are accessible.
     *
     * Example:
     * ```
     * {
     *     previewMode:      false,
     *     userStatus:       "subscribed"
     *     subscriptionType: "premium"
     * }
     * ```
     *
     * @returns  {Object}  An object with all properties that influence a users'
     *                     privileges.
     * @public
     */
    getUserPrivileges: function() {
        var user = this.get("user");

        return {
            previewMode:      get(user, "preview_mode", false),
            userStatus:       get(user, "status", null),
            subscriptionType: get(user, "subscription.type", null)
        };
    },

    // ----------------------------------
    // Set session state
    // ----------------------------------

    /**
     * Mark login status as pending.
     *
     * @private
     */
    setPending: function() {
        if (this.isLoggedIn !== 0) {
            this.set("isLoggedIn", 0);
            this.emit("Auth::authenticating");
        }
    },

    /**
     * Marks user as logged in, this also commits new `sessionKey` and `user`
     * values.
     *
     * @param  {string}  sessionKey  The session key
     * @param  {Object}  user        The user data
     * @private
     */
    setLoggedIn: function(sessionKey, user) {
        try {
            config.ottApi.useLocalStorage && localStorage.setItem("ottApiV2SessionKey", sessionKey);
        }
        catch (error) {
            // Might be blocked by client browser
        }

        this.set("sessionKey", sessionKey);
        this.set("user", user);

        if (this.isLoggedIn !== 1) {
            this.set("isLoggedIn", 1);
            this.emit("Auth::loggedIn");
        }
    },

    /**
     * Update our local copy of the user.
     *
     * @param  {Object}  user  The user data
     * @private
     */
    updateUser: function(user) {
        if (user) {
            this.set("user", user);
        }
        else {
            this.setLoggedOut();
        }
    },

    /**
     * Marks user as logged out, this also clears the `sessionKey` and `user`
     * values.
     *
     * @private
     */
    setLoggedOut: function() {
        if (this.isLoggedIn !== -1) {
            try {
                config.ottApi.useLocalStorage && localStorage.removeItem("ottApiV2SessionKey");
            }
            catch (error) {
            // Might be blocked by client browser
            }
            this.set("sessionKey", null);
            this.set("user", null);
            this.set("isLoggedIn", -1);
            this.emit("Auth::loggedOut");
        }
    },

    // ----------------------------------
    // Session authentication
    // ----------------------------------

    /**
     * Resume a session by providing a session key.
     *
     * @param    {string}   sessionKey  The session key
     * @returns  {Promise}  A promise that will resolve with the result.
     * @private
     */
    resumeSession: function(sessionKey) {
        var $analyticsService = this.app.$.analyticsService,
            that              = this;

        // Set new login state as pending
        this.setPending();

        // Resume session by validating the session key and updating the user
        // state
        return UserSessionService
            .authenticate(sessionKey)
            .then(function(sessionResponse) {
                if (sessionResponse.status === "ok") {
                    return that.fetchUserState()
                        .then(function(userResponse) {
                            if (userResponse.status === "ok") {
                                that.setLoggedIn(
                                    sessionKey,
                                    userResponse.data
                                );
                                $analyticsService.sendEvent(
                                    ["accepted", "analyticsCookies"],
                                    "Authentication",
                                    "resume_session_success"
                                );

                                return Promise.resolve({
                                    status: "ok"
                                });
                            }
                            else {
                                that.setLoggedOut();
                                $analyticsService.sendEvent(
                                    ["accepted", "analyticsCookies"],
                                    "Authentication",
                                    "resume_session_fail",
                                    userResponse.type || "unknown_error",
                                    null,
                                    {
                                        error: userResponse.type || "unknown_error"
                                    }
                                );

                                return Promise.resolve({
                                    status: "error",
                                    type:   userResponse.type || "unknown_error"
                                });
                            }
                        });
                }
                else {
                    that.setLoggedOut();
                    $analyticsService.sendEvent(
                        ["accepted", "analyticsCookies"],
                        "Authentication",
                        "resume_session_fail",
                        sessionResponse.type || "unknown_error",
                        null,
                        {
                            error: sessionResponse.type || "unknown_error"
                        }
                    );

                    return Promise.resolve({
                        status: "error",
                        type:   sessionResponse.type || "unknown_error"
                    });
                }
            })

            // Could resume session
            .catch(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                that.setLoggedOut();
                $analyticsService.sendEvent(
                    ["accepted", "analyticsCookies"],
                    "Authentication",
                    "resume_session_fail",
                    error.message || "unknown_error",
                    null,
                    {
                        error: error.message || "unknown_error"
                    }
                );

                return Promise.resolve({
                    status: "error",
                    type:   error.message || "unknown_error"
                });
            });
    },

    /**
     * Attempt to fetch the current `sessionKey`. If the session is valid, user
     * state will be updated automatically. If any part of the user state cannot
     * be updated the session will also be rejected.
     *
     * @returns  {Promise}  A promise that will resolve with the result.
     * @private
     */
    verifySession: function() {
        var that = this;

        // If we haven't got an authorized user, we explicitly mark login state
        // as pending.
        if (this.isLoggedIn !== 1) {
            this.setPending();
        }

        // Verify session by retrieving the session key and updating the user
        // state
        return UserSessionRepository
            .getSessionKey()
            .then(function(sessionResponse) {
                if (sessionResponse.status === "ok") {
                    return that.fetchUserState()
                        .then(function(userResponse) {
                            if (userResponse.status === "ok") {
                                that.setLoggedIn(
                                    sessionResponse.data,
                                    userResponse.data
                                );

                                return Promise.resolve({
                                    status: "ok"
                                });
                            }
                            else {
                                that.setLoggedOut();

                                return Promise.resolve({
                                    status: "error",
                                    type:   userResponse.type || "unknown_error"
                                });
                            }
                        });
                }
                else {
                    that.setLoggedOut();

                    return Promise.resolve({
                        status: "error",
                        type:   sessionResponse.type || "unknown_error"
                    });
                }
            })

            // Could not verify session
            .catch(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                that.setLoggedOut();

                return Promise.resolve({
                    status: "error",
                    type:   error.message || "unknown_error"
                });
            });
    },

    /**
     * Attempt to fetch the current user, including it's subscription.
     *
     * @returns  {Promise}  A promise that will resolve with the result.
     * @private
     */
    fetchUserState: function() {
        var that = this;

        return Promise
            .all([
                UserRepository.getUser(),
                UserSubscriptionRepository.getSubscription()
            ])
            .then(function(responses) {
                var user;

                // Cannot load user.
                if (responses[0].status !== "ok" || !responses[0].data) {
                    if (responses[0].type === "no_session") {
                        that.setLoggedOut();
                    }

                    return Promise.resolve({
                        status: "error",
                        type:   responses[0].type || "unknown_error"
                    });
                }

                // Build user
                user              = responses[0].data;
                user.subscription = null;

                // User is still pending, so we ignore subscription.
                if (user.status === "pending") {
                    return Promise.resolve({
                        status: "ok",
                        data:   user
                    });
                }

                // Cannot load subscription.
                if (responses[1].status !== "ok" || !responses[1].data) {
                    if (responses[1].type === "no_session") {
                        that.setLoggedOut();
                    }

                    return Promise.resolve({
                        status: "error",
                        type:   responses[1].type || "unknown_error"
                    });
                }

                // Finalize user
                user.subscription = responses[1].data;

                return Promise.resolve({
                    status: "ok",
                    data:   user
                });
            })

            // Could not load one or more components of the current user's state
            .catch(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                that.setLoggedOut();

                return Promise.resolve({
                    status: "error",
                    type:   error.message || "unknown_error"
                });
            });
    },

    /**
     * Lets the user log in with an e-mail address and password.
     *
     * @param    {string}   email     The email for authentication.
     * @param    {string}   password  The password for authentication.
     * @returns  {Promise}  A promise that will resolve with the result.
     * @public
     */
    emailLogin: function(email, password) {
        var $analyticsService = this.app.$.analyticsService,
            that              = this;

        // Set new login state as pending
        this.setPending();

        // Attempt login
        return UserSessionService
            .login(email, password)
            .then(function(response) {
                if (response.status === "ok") {
                    $analyticsService.sendEvent(
                        ["accepted", "analyticsCookies"],
                        "Authentication",
                        "email_login_success"
                    );

                    return that.verifySession();
                }
                else {
                    that.setLoggedOut();
                    $analyticsService.sendEvent(
                        ["accepted", "analyticsCookies"],
                        "Authentication",
                        "email_login_fail",
                        response.type || "unknown_error",
                        null,
                        {
                            error: response.type || "unknown_error"
                        }
                    );

                    return Promise.resolve({
                        status: "error",
                        type:   response.type || "unknown_error"
                    });
                }
            })

            // Could not authenticate user, assume session is invalid
            .catch(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                that.setLoggedOut();
                $analyticsService.sendEvent(
                    ["accepted", "analyticsCookies"],
                    "Authentication",
                    "email_login_fail",
                    error.message || "unknown_error",
                    null,
                    {
                        error: error.message || "unknown_error"
                    }
                );

                return Promise.resolve({
                    status: "error",
                    type:   error.message || "unknown_error"
                });
            });
    },

    /**
     * Attempt to stop the current session.
     *
     * @returns  {Promise}  A promise that will resolve with the result.
     * @public
     */
    logout: function() {
        var $analyticsService = this.app.$.analyticsService,
            that              = this;

        // Set new login state as pending
        this.setPending();

        // Send logout command to server, set local state to logged out when
        // server replies.
        return UserSessionService
            .logout()
            .then(function() {
                that.setLoggedOut();

                return Promise.resolve({
                    status: "ok"
                });
            })
            .catch(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                // If we can't verify the session, the session is dead.
                return that.verifySession()
                    .then(function(response) {
                        if (response.status === "ok") {
                            $analyticsService.sendEvent(
                                ["accepted", "analyticsCookies"],
                                "Authentication",
                                "logout_fail",
                                response.type || "unknown_error",
                                null,
                                {
                                    error: response.type || "unknown_error"
                                }
                            );

                            // We still have verified session, logout failed.
                            return Promise.resolve({
                                status: "error",
                                type:   "unknown_error"
                            });
                        }
                        else {
                            $analyticsService.sendEvent(
                                ["accepted", "analyticsCookies"],
                                "Authentication",
                                "logout_success"
                            );

                            // We can't verify the session. It seems we're at least
                            // logged out.
                            return Promise.resolve({
                                status: "ok"
                            });
                        }
                    });
            });
    },

    // ----------------------------------
    // Subscription Mutations
    // ----------------------------------

    /**
     * Attempt to cancel the current user's subscription.
     *
     * @returns  {Promise}  A promise that will resolve with the result.
     * @public
     */
    cancelSubscription: function() {
        var $analyticsService = this.app.$.analyticsService,
            that              = this;

        return UserSubscriptionService
            .cancelSubscription()
            .then(function(response) {
                if (response.status === "ok") {
                    $analyticsService.sendEvent(
                        ["accepted", "analyticsCookies"],
                        "Subscription",
                        "cancel_subscription_success"
                    );

                    // Ensure up-to-date user state
                    that.fetchUserState()
                        .then(function(userResponse) {
                            if (userResponse.status === "ok") {
                                that.updateUser(userResponse.data);
                            }
                            else {
                                that.setLoggedOut();
                            }
                        });
                }
                else if (response.status === "error") {
                    $analyticsService.sendEvent(
                        ["accepted", "analyticsCookies"],
                        "Subscription",
                        "cancel_subscription_fail",
                        response.type || "unknown_error",
                        null,
                        {
                            error: response.type || "unknown_error"
                        }
                    );

                    if (response.type === "no_session") {
                        that.setLoggedOut();
                    }
                    else {
                        // Ensure up-to-date user state
                        that.fetchUserState()
                            .then(function(userResponse) {
                                if (userResponse.status === "ok") {
                                    that.updateUser(userResponse.data);
                                }
                                else {
                                    that.setLoggedOut();
                                }
                            });
                    }
                }

                return Promise.resolve(response);
            })
            .catch(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                that.verifySession();
                $analyticsService.sendEvent(
                    ["accepted", "analyticsCookies"],
                    "Subscription",
                    "cancel_subscription_fail",
                    error.message || "unknown_error",
                    null,
                    {
                        error: error.message || "unknown_error"
                    }
                );

                return Promise.resolve({
                    status: "error",
                    type:   error.message || "unknown_error"
                });
            });
    },

    /**
     * Attempt to reactivate the current user's subscription.
     *
     * @returns  {Promise}  A promise that will resolve with the result.
     * @public
     */
    reactivateSubscription: function() {
        var $analyticsService = this.app.$.analyticsService,
            that              = this;

        return UserSubscriptionService
            .reactivateSubscription()
            .then(function(response) {
                if (response.status === "ok") {
                    $analyticsService.sendEvent(
                        ["accepted", "analyticsCookies"],
                        "Subscription",
                        "reactivate_subscription_success"
                    );

                    // Ensure up-to-date user state
                    that.fetchUserState()
                        .then(function(userResponse) {
                            if (userResponse.status === "ok") {
                                that.updateUser(userResponse.data);
                            }
                            else {
                                that.setLoggedOut();
                            }
                        });
                }
                else if (response.status === "error") {
                    $analyticsService.sendEvent(
                        ["accepted", "analyticsCookies"],
                        "Subscription",
                        "reactivate_subscription_fail",
                        response.type || "unknown_error",
                        null,
                        {
                            error: response.type || "unknown_error"
                        }
                    );

                    if (response.type === "no_session") {
                        that.setLoggedOut();
                    }
                    else {
                        // Ensure up-to-date user state
                        that.fetchUserState()
                            .then(function(userResponse) {
                                if (userResponse.status === "ok") {
                                    that.updateUser(userResponse.data);
                                }
                                else {
                                    that.setLoggedOut();
                                }
                            });
                    }
                }

                return Promise.resolve(response);
            })
            .catch(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                that.verifySession();
                $analyticsService.sendEvent(
                    ["accepted", "analyticsCookies"],
                    "Subscription",
                    "reactivate_subscription_fail",
                    error.message || "unknown_error",
                    null,
                    {
                        error: error.message || "unknown_error"
                    }
                );

                return Promise.resolve({
                    status: "error",
                    type:   error.message || "unknown_error"
                });
            });
    },

    // ----------------------------------
    // Profile Mutations
    // ----------------------------------

    /**
     * Attempt to change the user's profile.
     *
     * @param    {Object}   changes   Key-value pairs to patch the profile with
     * @param    {?string}  password  Optional verification by password
     * @returns  {Promise}  A promise that will resolve with the result.
     * @public
     */
    changeProfile: function(changes, password) {
        var $analyticsService = this.app.$.analyticsService,
            that              = this;

        return UserService
            .changeProfile(changes, password)
            .then(function(response) {
                if (response.status === "ok") {
                    $analyticsService.sendEvent(
                        ["accepted", "analyticsCookies"],
                        "Subscription",
                        "change_profile_success"
                    );

                    // Ensure up-to-date user state
                    that.fetchUserState()
                        .then(function(userResponse) {
                            if (userResponse.status === "ok") {
                                that.updateUser(userResponse.data);
                            }
                            else {
                                that.setLoggedOut();
                            }
                        });
                }
                else if (response.status === "error") {
                    $analyticsService.sendEvent(
                        ["accepted", "analyticsCookies"],
                        "Subscription",
                        "change_profile_fail",
                        response.type || "unknown_error",
                        null,
                        {
                            error: response.type || "unknown_error"
                        }
                    );

                    if (response.type === "no_session") {
                        that.setLoggedOut();
                    }
                    else {
                        // Ensure up-to-date user state
                        that.fetchUserState()
                            .then(function(userResponse) {
                                if (userResponse.status === "ok") {
                                    that.updateUser(userResponse.data);
                                }
                                else {
                                    that.setLoggedOut();
                                }
                            });
                    }
                }

                return Promise.resolve(response);
            })
            .catch(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                that.verifySession();
                $analyticsService.sendEvent(
                    ["accepted", "analyticsCookies"],
                    "Subscription",
                    "change_profile_fail",
                    error.message || "unknown_error",
                    null,
                    {
                        error: error.message || "unknown_error"
                    }
                );

                return Promise.resolve({
                    status: "error",
                    type:   error.message || "unknown_error"
                });
            });
    },

    /**
     * Toggle preview mode.
     *
     * @param    {string}   toggle    Whether preview mode should be turned on
     * @returns  {Promise}  A promise that will resolve with the result.
     * @public
     */
    togglePreviewMode: function(toggle) {
        var that = this;

        return UserService
            .changeProfile({
                preview_mode: toggle
            })
            .then(function(response) {
                if (response.status === "ok") {
                    // Ensure up-to-date user state
                    that.fetchUserState()
                        .then(function(userResponse) {
                            if (userResponse.status === "ok") {
                                that.updateUser(userResponse.data);
                            }
                            else {
                                that.setLoggedOut();
                            }
                        });
                }
                else if (response.status === "error") {
                    if (response.type === "no_session") {
                        that.setLoggedOut();
                    }
                    else {
                        // Ensure up-to-date user state
                        that.fetchUserState()
                            .then(function(userResponse) {
                                if (userResponse.status === "ok") {
                                    that.updateUser(userResponse.data);
                                }
                                else {
                                    that.setLoggedOut();
                                }
                            });
                    }
                }

                return Promise.resolve(response);
            })
            .catch(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                that.verifySession();

                return Promise.resolve({
                    status: "error",
                    type:   error.message || "unknown_error"
                });
            });
    },

    /**
     * Attempt to save the preferred language for the current user.
     *
     * @param    {string}   language  The language
     * @returns  {Promise}  A promise that will resolve with the result.
     * @public
     */
    setLanguagePreference: function(language) {
        var $analyticsService = this.app.$.analyticsService,
            that              = this;

        return UserService
            .changeProfile({
                language: language
            })
            .then(function(response) {
                if (response.status === "ok") {
                    $analyticsService.sendEvent(
                        ["accepted", "analyticsCookies"],
                        "Subscription",
                        "language_preference_success"
                    );

                    // Ensure up-to-date user state
                    that.fetchUserState()
                        .then(function(userResponse) {
                            if (userResponse.status === "ok") {
                                that.updateUser(userResponse.data);
                            }
                            else {
                                that.setLoggedOut();
                            }
                        });
                }
                else if (response.status === "error") {
                    $analyticsService.sendEvent(
                        ["accepted", "analyticsCookies"],
                        "Subscription",
                        "language_preference_fail",
                        response.type || "unknown_error",
                        null,
                        {
                            error: response.type || "unknown_error"
                        }
                    );

                    if (response.type === "no_session") {
                        that.setLoggedOut();
                    }
                    else {
                        // Ensure up-to-date user state
                        that.fetchUserState()
                            .then(function(userResponse) {
                                if (userResponse.status === "ok") {
                                    that.updateUser(userResponse.data);
                                }
                                else {
                                    that.setLoggedOut();
                                }
                            });
                    }
                }

                return Promise.resolve(response);
            })
            .catch(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                that.verifySession();
                $analyticsService.sendEvent(
                    ["accepted", "analyticsCookies"],
                    "Subscription",
                    "language_preference_fail",
                    error.message || "unknown_error",
                    null,
                    {
                        error: error.message || "unknown_error"
                    }
                );

                return Promise.resolve({
                    status: "error",
                    type:   error.message || "unknown_error"
                });
            });
    },

    /**
     * Attempt to request a password reset email.
     *
     * @param    {string}   email  The email
     * @returns  {Promise}  A promise that will resolve with the result.
     * @public
     */
    requestPasswordReset: function(email) {
        var $analyticsService = this.app.$.analyticsService;

        return UserSessionService
            .requestPasswordReset(email)
            .then(function(response) {
                if (response.status === "ok") {
                    $analyticsService.sendEvent(
                        ["accepted", "analyticsCookies"],
                        "Authentication",
                        "request_password_reset_success"
                    );
                }
                else if (response.status === "error") {
                    $analyticsService.sendEvent(
                        ["accepted", "analyticsCookies"],
                        "Authentication",
                        "request_password_reset_fail",
                        response.type || "unknown_error",
                        null,
                        {
                            error: response.type || "unknown_error"
                        }
                    );
                }

                return Promise.resolve(response);
            })
            .catch(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                $analyticsService.sendEvent(
                    ["accepted", "analyticsCookies"],
                    "Authentication",
                    "request_password_reset_fail",
                    error.message || "unknown_error",
                    null,
                    {
                        error: error.message || "unknown_error"
                    }
                );

                return Promise.resolve({
                    status: "error",
                    type:   error.message || "unknown_error"
                });
            });
    },

    /**
     * @returns  {Promise}  A promise that will resolve with the result.
     * @public
     */
    removeAccount: function() {
        var $analyticsService = this.app.$.analyticsService,
            that              = this;

        return UserService
            .removeAccount()
            .then(function(response) {
                if (response.status === "ok") {
                    $analyticsService.sendEvent(
                        ["accepted", "analyticsCookies"],
                        "Authentication",
                        "remove_account_success"
                    );

                    // Account removed, time to log out
                    that.setLoggedOut();
                }
                else if (response.status === "error") {
                    $analyticsService.sendEvent(
                        ["accepted", "analyticsCookies"],
                        "Authentication",
                        "remove_account_fail",
                        response.type || "unknown_error",
                        null,
                        {
                            error: response.type || "unknown_error"
                        }
                    );

                    if (response.type === "no_session") {
                        that.setLoggedOut();
                    }
                    else {
                        // Ensure up-to-date user state
                        that.fetchUserState()
                            .then(function(userResponse) {
                                if (userResponse.status === "ok") {
                                    that.updateUser(userResponse.data);
                                }
                                else {
                                    that.setLoggedOut();
                                }
                            });
                    }
                }

                return Promise.resolve(response);
            })
            .catch(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                that.verifySession();
                $analyticsService.sendEvent(
                    ["accepted", "analyticsCookies"],
                    "Authentication",
                    "remove_account_fail",
                    error.message || "unknown_error",
                    null,
                    {
                        error: error.message || "unknown_error"
                    }
                );

                return Promise.resolve({
                    status: "error",
                    type:   error.message || "unknown_error"
                });
            });
    },

    /**
     * Stop session when service gets destroyed.
     *
     * @method
     * @private
     */
    destroy: kind.inherit(function(sup) {
        return function() {
            UserSessionService.logout();
            this.setLoggedOut();
            sup.apply(this, arguments);
        };
    })

});
